import {
  BarChart,
  Bar,
  LineChart,
  Line,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from "recharts";
import type { VizType, LookerWidgetData } from "@ligament/shared";

const COLORS = [
  "#3b82f6",
  "#10b981",
  "#f59e0b",
  "#ef4444",
  "#8b5cf6",
  "#ec4899",
  "#06b6d4",
  "#84cc16",
];

interface WidgetChartProps {
  vizType: VizType;
  data: LookerWidgetData;
  config?: Record<string, unknown>;
}

// Looker field names are fully-qualified like
// "fct_leads_funnel_marketing_phi_exclude.count". The server humanizes them
// ("fct leads funnel marketing phi exclude > count"), which still overflows
// small tiles. For chart legends/axes we only want the measure half, prettified.
function shortFieldLabel(fieldName: string, fallbackLabel?: string): string {
  const tail = fieldName.includes(".") ? fieldName.split(".").pop()! : fieldName;
  const cleaned = tail.replace(/_/g, " ").trim();
  if (!cleaned) return fallbackLabel ?? fieldName;
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
}

export function WidgetChart({ vizType, data }: WidgetChartProps) {
  if (!data.data || data.data.length === 0) {
    return (
      <p className="text-xs text-muted-foreground text-center py-8">No data</p>
    );
  }

  const fields = data.fields;
  const dimensionField = fields[0]?.name;
  const measureFields = fields.slice(1).map((f) => f.name);
  const labelMap = Object.fromEntries(
    fields.map((f) => [f.name, shortFieldLabel(f.name, f.label)]),
  );
  // Hide the legend entirely when there's only one measure series — the chart
  // title already names what's being shown.
  const showLegend = measureFields.length > 1;

  // Cap rendered data to keep small tiles readable. Catalog queries already
  // ask Looker for a sensible top-N, but some saved widgets predate that or
  // come from agent-proposed metrics with no bound. Show a "top N of M" hint
  // when we truncate so the user knows there's more.
  const RENDER_CAP: Record<string, number> = {
    bar: 15,
    line: 30,
    area: 30,
    pie: 8,
    table: 50,
  };
  const cap = RENDER_CAP[vizType] ?? data.data.length;
  const totalRows = data.data.length;
  const visibleRows = data.data.slice(0, cap);
  const wasTruncated = totalRows > visibleRows.length;

  const chartData = visibleRows.map((row) => {
    const entry: Record<string, unknown> = {};
    for (const f of fields) {
      entry[f.name] = row[f.name];
    }
    return entry;
  });

  switch (vizType) {
    case "single_value": {
      const value = chartData[0]?.[measureFields[0] ?? dimensionField];
      const label = labelMap[measureFields[0] ?? dimensionField] ?? "";
      return (
        <div className="flex flex-col items-center justify-center h-full">
          <p className="text-3xl font-semibold tracking-tight">
            {typeof value === "number" ? value.toLocaleString() : String(value ?? "-")}
          </p>
          <p className="text-sm text-muted-foreground mt-1">{label}</p>
        </div>
      );
    }

    case "bar":
      return (
        <div className="relative h-full w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 24 }}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
              <XAxis
                dataKey={dimensionField}
                tick={{ fontSize: 10 }}
                interval={0}
                angle={-30}
                textAnchor="end"
                height={48}
              />
              <YAxis tick={{ fontSize: 10 }} width={40} />
              <Tooltip />
              {showLegend && <Legend />}
              {measureFields.map((field, i) => (
                <Bar
                  key={field}
                  dataKey={field}
                  name={labelMap[field]}
                  fill={COLORS[i % COLORS.length]}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
          {wasTruncated && (
            <span className="absolute top-1 right-2 text-[10px] text-muted-foreground">
              Top {visibleRows.length} of {totalRows}
            </span>
          )}
        </div>
      );

    case "line":
      return (
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
            <XAxis dataKey={dimensionField} tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip />
            {showLegend && <Legend />}
            {measureFields.map((field, i) => (
              <Line
                key={field}
                type="monotone"
                dataKey={field}
                name={labelMap[field]}
                stroke={COLORS[i % COLORS.length]}
                strokeWidth={2}
                dot={false}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      );

    case "area":
      return (
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
            <XAxis dataKey={dimensionField} tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip />
            {showLegend && <Legend />}
            {measureFields.map((field, i) => (
              <Area
                key={field}
                type="monotone"
                dataKey={field}
                name={labelMap[field]}
                stroke={COLORS[i % COLORS.length]}
                fill={COLORS[i % COLORS.length]}
                fillOpacity={0.15}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      );

    case "pie": {
      const pieDataKey = measureFields[0] ?? dimensionField;
      // Render the legend manually as a flex row of slice swatches. Recharts'
      // built-in Legend keeps appending the Pie's own series entry (the raw
      // measure field name) no matter what payload/legendType combination we
      // pass — controlling it ourselves is the only reliable way.
      return (
        <div className="flex flex-col h-full">
          <div className="flex-1 min-h-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData}
                  dataKey={pieDataKey}
                  nameKey={dimensionField}
                  cx="50%"
                  cy="50%"
                  outerRadius="85%"
                  isAnimationActive={false}
                >
                  {chartData.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex flex-wrap gap-x-3 gap-y-1 justify-center pt-2 text-[11px]">
            {chartData.map((row, i) => (
              <div key={i} className="flex items-center gap-1.5">
                <span
                  className="inline-block w-2.5 h-2.5 rounded-sm"
                  style={{ background: COLORS[i % COLORS.length] }}
                />
                <span className="text-muted-foreground">
                  {String(row[dimensionField] ?? "")}
                </span>
              </div>
            ))}
          </div>
        </div>
      );
    }

    case "table":
      return (
        <div className="overflow-auto max-h-full text-xs">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                {fields.map((f) => (
                  <th
                    key={f.name}
                    className="text-left py-1.5 px-2 font-medium text-muted-foreground"
                  >
                    {f.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {chartData.map((row: Record<string, unknown>, i: number) => (
                <tr key={i} className="border-b border-border/50">
                  {fields.map((f: { name: string; label: string; type: string }) => (
                    <td key={f.name} className="py-1.5 px-2">
                      {typeof row[f.name] === "number"
                        ? (row[f.name] as number).toLocaleString()
                        : String(row[f.name] ?? "")}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );

    default:
      return (
        <p className="text-xs text-muted-foreground">
          Unsupported chart type: {vizType}
        </p>
      );
  }
}
